// ReportsPage.jsx - Rapports et statistiques
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  BarChart3, PieChart, TrendingUp, Download, RefreshCw,
  Users, Briefcase, DollarSign, Building2, Scale, FileText
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import api from '../lib/api';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const DRC_PROVINCES = [
  "KINSHASA", "KONGO-CENTRAL", "KWANGO", "KWILU", "MAI-NDOMBE",
  "KASAI", "KASAI-CENTRAL", "KASAI-ORIENTAL", "LOMAMI", "SANKURU",
  "MANIEMA", "SUD-KIVU", "NORD-KIVU", "ITURI", "HAUT-UELE",
  "TSHOPO", "BAS-UELE", "NORD-UBANGI", "SUD-UBANGI", "MONGALA",
  "EQUATEUR", "TSHUAPA", "TANGANYIKA", "HAUT-LOMAMI", "LUALABA", "HAUT-KATANGA"
];

const ReportsPage = () => {
  const { language, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [filterProvince, setFilterProvince] = useState('all');
  
  const [dashboardStats, setDashboardStats] = useState(null);
  const [prisonersReport, setPrisonersReport] = useState(null);
  const [casesReport, setCasesReport] = useState(null);
  const [paymentsReport, setPaymentsReport] = useState(null);
  const [usersReport, setUsersReport] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      const response = await api.get('/reports/dashboard');
      setDashboardStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du tableau de bord');
    }
  }, []);

  const loadPrisonersReport = useCallback(async () => {
    try {
      const params = filterProvince !== 'all' ? `?province=${filterProvince}` : '';
      const response = await api.get(`/reports/prisoners${params}`);
      setPrisonersReport(response.data);
    } catch (error) {
      toast.error('Erreur');
    }
  }, [filterProvince]);

  const loadCasesReport = useCallback(async () => {
    try {
      const params = filterProvince !== 'all' ? `?province=${filterProvince}` : '';
      const response = await api.get(`/reports/cases${params}`);
      setCasesReport(response.data);
    } catch (error) {
      toast.error('Erreur');
    }
  }, [filterProvince]);

  const loadPaymentsReport = useCallback(async () => {
    try {
      const response = await api.get('/reports/payments');
      setPaymentsReport(response.data);
    } catch (error) {
      toast.error('Erreur');
    }
  }, []);

  const loadUsersReport = useCallback(async () => {
    try {
      const response = await api.get('/reports/users');
      setUsersReport(response.data);
    } catch (error) {
      console.log('Users report not available');
    }
  }, []);

  const loadAllReports = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadDashboard(),
      loadPrisonersReport(),
      loadCasesReport(),
      loadPaymentsReport(),
      loadUsersReport()
    ]);
    setLoading(false);
  }, [loadDashboard, loadPrisonersReport, loadCasesReport, loadPaymentsReport, loadUsersReport]);

  useEffect(() => {
    loadAllReports();
  }, [loadAllReports]);

  useEffect(() => {
    if (activeTab === 'prisoners') loadPrisonersReport();
    if (activeTab === 'cases') loadCasesReport();
  }, [filterProvince, activeTab, loadPrisonersReport, loadCasesReport]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const objectToChartData = (obj, labelKey = 'name', valueKey = 'value') => {
    if (!obj) return [];
    return Object.entries(obj).map(([key, value]) => ({
      [labelKey]: key,
      [valueKey]: typeof value === 'object' ? value.count || value.amount || 0 : value
    }));
  };

  const renderStatCard = (title, value, icon: React.ReactNode, color = 'blue', subtitle = '') => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 bg-${color}-100 dark:bg-${color}-900 rounded-full flex items-center justify-center`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              {language === 'fr' ? 'Rapports & Statistiques' : 'Reports & Statistics'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Analyses et indicateurs de performance' : 'Analytics and performance indicators'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterProvince} onValueChange={setFilterProvince}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Province" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'fr' ? 'Toutes les provinces' : 'All provinces'}</SelectItem>
                {DRC_PROVINCES.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadAllReports} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {language === 'fr' ? 'Actualiser' : 'Refresh'}
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
            <TabsTrigger value="dashboard">{language === 'fr' ? 'Tableau de bord' : 'Dashboard'}</TabsTrigger>
            <TabsTrigger value="prisoners">{language === 'fr' ? 'Détenus' : 'Prisoners'}</TabsTrigger>
            <TabsTrigger value="cases">{language === 'fr' ? 'Dossiers' : 'Cases'}</TabsTrigger>
            <TabsTrigger value="payments">{language === 'fr' ? 'Paiements' : 'Payments'}</TabsTrigger>
            <TabsTrigger value="users">{language === 'fr' ? 'Utilisateurs' : 'Users'}</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {dashboardStats && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {renderStatCard(
                    language === 'fr' ? 'Total Dossiers' : 'Total Cases',
                    dashboardStats.cases?.total || 0,
                    <Briefcase className="w-6 h-6 text-blue-600" />,
                    'blue',
                    `${dashboardStats.cases?.open || 0} ${language === 'fr' ? 'ouverts' : 'open'}`
                  )}
                  {renderStatCard(
                    language === 'fr' ? 'Total Détenus' : 'Total Prisoners',
                    dashboardStats.prisoners?.total || 0,
                    <Users className="w-6 h-6 text-orange-600" />,
                    'orange',
                    `${dashboardStats.prisoners?.preventive || 0} ${language === 'fr' ? 'préventive' : 'preventive'}`
                  )}
                  {renderStatCard(
                    language === 'fr' ? 'Plaintes' : 'Complaints',
                    dashboardStats.complaints?.total || 0,
                    <FileText className="w-6 h-6 text-purple-600" />,
                    'purple',
                    `${dashboardStats.complaints?.pending || 0} ${language === 'fr' ? 'en attente' : 'pending'}`
                  )}
                  {renderStatCard(
                    language === 'fr' ? 'Paiements reçus' : 'Payments Received',
                    formatCurrency(dashboardStats.payments?.paid_amount),
                    <DollarSign className="w-6 h-6 text-green-600" />,
                    'green',
                    `${formatCurrency(dashboardStats.payments?.pending_amount)} ${language === 'fr' ? 'en attente' : 'pending'}`
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Répartition des Dossiers' : 'Cases Distribution'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <RechartsPieChart>
                          <Pie
                            data={[
                              { name: language === 'fr' ? 'Ouverts' : 'Open', value: dashboardStats.cases?.open || 0 },
                              { name: language === 'fr' ? 'Clôturés' : 'Closed', value: dashboardStats.cases?.closed || 0 }
                            ]}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label
                          >
                            {[0, 1].map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Statut des Détenus' : 'Prisoner Status'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <RechartsPieChart>
                          <Pie
                            data={[
                              { name: language === 'fr' ? 'Préventive' : 'Preventive', value: dashboardStats.prisoners?.preventive || 0 },
                              { name: language === 'fr' ? 'Condamnés' : 'Sentenced', value: dashboardStats.prisoners?.sentenced || 0 }
                            ]}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label
                          >
                            {[0, 1].map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index + 2]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Prisoners Tab */}
          <TabsContent value="prisoners" className="space-y-6">
            {prisonersReport && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {renderStatCard(language === 'fr' ? 'Total' : 'Total', prisonersReport.total, <Users className="w-6 h-6 text-blue-600" />)}
                  {renderStatCard(language === 'fr' ? 'Hommes' : 'Male', prisonersReport.by_gender?.M || 0, <Users className="w-6 h-6 text-cyan-600" />)}
                  {renderStatCard(language === 'fr' ? 'Femmes' : 'Female', prisonersReport.by_gender?.F || 0, <Users className="w-6 h-6 text-pink-600" />)}
                  {renderStatCard(language === 'fr' ? 'Préventive' : 'Preventive', prisonersReport.by_status?.PREVENTIVE || 0, <Users className="w-6 h-6 text-orange-600" />)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Par Statut' : 'By Status'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={objectToChartData(prisonersReport.by_status)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Par Type de Crime' : 'By Crime Type'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={objectToChartData(prisonersReport.by_crime_type)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={100} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Admissions Mensuelles' : 'Monthly Admissions'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={objectToChartData(prisonersReport.monthly_admissions)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases" className="space-y-6">
            {casesReport && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {renderStatCard(language === 'fr' ? 'Total Dossiers' : 'Total Cases', casesReport.total, <Briefcase className="w-6 h-6 text-blue-600" />)}
                  {renderStatCard(language === 'fr' ? 'Ouverts' : 'Open', casesReport.by_status?.OPEN || 0, <Briefcase className="w-6 h-6 text-green-600" />)}
                  {renderStatCard(language === 'fr' ? 'En cours' : 'In Progress', casesReport.by_status?.IN_PROGRESS || 0, <Briefcase className="w-6 h-6 text-yellow-600" />)}
                  {renderStatCard(language === 'fr' ? 'Clôturés' : 'Closed', casesReport.by_status?.CLOSED || 0, <Briefcase className="w-6 h-6 text-gray-600" />)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Par Type de Dossier' : 'By Case Type'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={objectToChartData(casesReport.by_type)}
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            label
                          >
                            {objectToChartData(casesReport.by_type).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Par Province' : 'By Province'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={objectToChartData(casesReport.by_province).slice(0, 10)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="value" fill="#8b5cf6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Évolution Mensuelle' : 'Monthly Trend'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={objectToChartData(casesReport.monthly_cases)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            {paymentsReport && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {renderStatCard(language === 'fr' ? 'Total Collecté' : 'Total Collected', formatCurrency(paymentsReport.totals?.total_amount), <DollarSign className="w-6 h-6 text-blue-600" />)}
                  {renderStatCard(language === 'fr' ? 'Payé' : 'Paid', formatCurrency(paymentsReport.totals?.paid_amount), <DollarSign className="w-6 h-6 text-green-600" />)}
                  {renderStatCard(language === 'fr' ? 'En Attente' : 'Pending', formatCurrency(paymentsReport.totals?.pending_amount), <DollarSign className="w-6 h-6 text-yellow-600" />)}
                  {renderStatCard(language === 'fr' ? 'Transactions' : 'Transactions', paymentsReport.totals?.total_count || 0, <TrendingUp className="w-6 h-6 text-purple-600" />)}
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>{language === 'fr' ? 'Revenus Mensuels' : 'Monthly Revenue'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={Object.entries(paymentsReport.monthly_revenue || {}).map(([month, data]) => ({
                        name: month,
                        amount: data.amount,
                        count: data.count
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" orientation="left" stroke="#10b981" />
                        <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" />
                        <Tooltip formatter={(value, name) => name === 'amount' ? formatCurrency(value) : value} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="amount" name={language === 'fr' ? 'Montant' : 'Amount'} fill="#10b981" />
                        <Bar yAxisId="right" dataKey="count" name={language === 'fr' ? 'Transactions' : 'Transactions'} fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {usersReport && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {renderStatCard(language === 'fr' ? 'Total Utilisateurs' : 'Total Users', usersReport.total, <Users className="w-6 h-6 text-blue-600" />)}
                  {renderStatCard(language === 'fr' ? 'Actifs' : 'Active', usersReport.active, <Users className="w-6 h-6 text-green-600" />)}
                  {renderStatCard(language === 'fr' ? 'Inactifs' : 'Inactive', usersReport.inactive, <Users className="w-6 h-6 text-red-600" />)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Par Rôle' : 'By Role'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={objectToChartData(usersReport.by_role)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={150} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#8b5cf6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>{language === 'fr' ? 'Par Niveau Territorial' : 'By Territorial Level'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPieChart>
                          <Pie
                            data={objectToChartData(usersReport.by_level)}
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            label
                          >
                            {objectToChartData(usersReport.by_level).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
